import "./TimeTracking.scss";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseIcon from "@mui/icons-material/Pause";
import FileDownloadDoneIcon from "@mui/icons-material/FileDownloadDone";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useSelector } from "react-redux";
import { selectCargoTimeTracking } from "../../../ducks/data/route-with-stops/selectors";
import { useAppDispatch } from "../../../redux-store";
import { useState } from "react";
import {
  pauseCargoTimeTracking,
  resumeCargoTimeTracking,
  startCargoTimeTracking,
  stopCargoTimeTracking,
} from "../../../ducks/app/cargo-time-tracking";
import { DateTime } from "luxon";
import { useParams } from "react-router";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { Alert, CircularProgress, DialogActions } from "@mui/material";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import { useRoutePageContext } from "../RoutePageContext";
import { useTranslation } from "react-i18next";
import TotalCargoDriverTime from "./TotalCargoDriverTime";
import { DateInput, TimeInput } from "../../../components/TimeInput";
import {
  selectCargoPauseResumeTrackingInProgress,
  selectCargoStartTrackingInProgress,
  selectCargoStopTrackingInProgress,
} from "../../../ducks/app/cargo-time-tracking/selectors";
import LoadingButton from "../../../components/LoadingButton";
import {
  ActionErrorKey,
  handleActionError,
} from "../../../helpers/actionError";

const TimeTracking = (input: { cargoId: string; stopId: string }) => {
  const { cargoId, stopId } = input;
  const { t } = useTranslation(["components", "stopPage", "translation"]);
  const dispatch = useAppDispatch();
  const [errorKey, setErrorKey] = useState<ActionErrorKey | null>(null);
  const startRequestInProgress = useSelector(
    selectCargoStartTrackingInProgress(cargoId)
  );
  const pauseResumeRequestInProgress = useSelector(
    selectCargoPauseResumeTrackingInProgress(cargoId)
  );
  const stopRequestInProgress = useSelector(
    selectCargoStopTrackingInProgress(cargoId)
  );
  const [shouldShowEditStartedTimeDialog, setShouldShowEditStartedTimeDialog] =
    useState(false);
  const [shouldShowEditEndedTimeDialog, setShouldShowEditEndedTimeDialog] =
    useState(false);
  const params = useParams();
  const routeId = params.routeId as string;
  const truckId = params.truckId as string;
  const tracking = useSelector(selectCargoTimeTracking(input.cargoId));
  const { reloadData } = useRoutePageContext();

  if (!tracking) {
    return null;
  }

  const lastPausedResumedEvent = tracking.pauseResume.length
    ? tracking.pauseResume[tracking.pauseResume.length - 1]
    : null;

  const latestPause =
    tracking.pauseResume[tracking.pauseResume.length - 1]?.type === "PAUSE"
      ? tracking.pauseResume[tracking.pauseResume.length - 1]
      : null;

  const runTrackingAction = async (
    action: typeof startCargoTimeTracking,
    reloadOnSuccess: boolean
  ) => {
    setErrorKey(null);
    try {
      await dispatch(
        action({
          cargoId,
          data: {
            time: DateTime.now(),
            routeId,
            stopId,
            truckId,
          },
        })
      ).unwrap();
      if (reloadOnSuccess) {
        reloadData();
      }
    } catch (err) {
      setErrorKey(handleActionError(err));
      // A request that timed out may still have been applied server-side
      // (e.g. sent right before the app was suspended), so show the real state.
      reloadData();
    }
  };

  const onStartTrackingClick = async () => {
    if (tracking.started) {
      setShouldShowEditStartedTimeDialog(true);
      return;
    }
    await runTrackingAction(startCargoTimeTracking, true);
  };

  const onPauseTrackingClick = () =>
    runTrackingAction(pauseCargoTimeTracking, true);

  const onResumeTrackingClick = () =>
    runTrackingAction(resumeCargoTimeTracking, true);

  const onStopTrackingClick = async () => {
    if (tracking.ended) {
      setShouldShowEditEndedTimeDialog(true);
      return;
    }
    await runTrackingAction(stopCargoTimeTracking, false);
  };

  const requestInProgress =
    startRequestInProgress ||
    pauseResumeRequestInProgress ||
    stopRequestInProgress;

  const pauseResumeEventsTimes = tracking.pauseResume.map((x) =>
    DateTime.fromISO(x.time)
  );

  return (
    <>
      {shouldShowEditStartedTimeDialog && (
        <EditTimeTrackingValueDialog
          type="START"
          cargoId={cargoId}
          stopId={stopId}
          dateTime={DateTime.fromISO(tracking.started!)}
          onClose={() => setShouldShowEditStartedTimeDialog(false)}
          pauseResumeEventsTimes={pauseResumeEventsTimes}
        />
      )}
      {shouldShowEditEndedTimeDialog && (
        <EditTimeTrackingValueDialog
          type="END"
          cargoId={cargoId}
          stopId={stopId}
          dateTime={DateTime.fromISO(tracking.ended!)}
          onClose={() => setShouldShowEditEndedTimeDialog(false)}
          pauseResumeEventsTimes={pauseResumeEventsTimes}
        />
      )}

      <div className="cargo-time-tracking">
        {tracking.started ? (
          <button
            disabled={requestInProgress}
            className="time-tracking-button flex-grow gray"
            onClick={onStartTrackingClick}
          >
            <div className="flex flex-col items-end">
              <div>{t("stopPage:timeTracking.started")}</div>
              <div className="time-tracking-button__date">
                {DateTime.fromISO(tracking.started).toFormat("HH:mm")}
              </div>
            </div>
            <div>
              <EditOutlinedIcon style={{ fontSize: 22 }} />
            </div>
          </button>
        ) : (
          <button
            disabled={requestInProgress}
            className="time-tracking-button flex-grow green"
            onClick={onStartTrackingClick}
          >
            {startRequestInProgress ? (
              <LoadingSpinner />
            ) : (
              <PlayCircleOutlineIcon style={{ fontSize: 20 }} />
            )}
            <div>
              {startRequestInProgress
                ? t("components:buttonLabels.gettingGps")
                : t("stopPage:timeTracking.start")}
            </div>
          </button>
        )}

        <button
          disabled={
            !!(requestInProgress || !tracking.started || tracking.ended)
          }
          onClick={
            lastPausedResumedEvent?.type === "PAUSE"
              ? onResumeTrackingClick
              : onPauseTrackingClick
          }
          className="time-tracking-button flex-grow yellow"
        >
          {lastPausedResumedEvent?.type === "PAUSE" ? (
            <>
              <div>
                {pauseResumeRequestInProgress ? (
                  <LoadingSpinner />
                ) : (
                  <PlayCircleOutlineIcon style={{ fontSize: 20 }} />
                )}
              </div>
              <div className="flex flex-col items-start">
                <div>{t("stopPage:timeTracking.resume")}</div>
                <div className="time-tracking-button__date">
                  {DateTime.fromISO(lastPausedResumedEvent.time).toFormat(
                    "HH:mm"
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {pauseResumeRequestInProgress ? (
                <LoadingSpinner />
              ) : (
                <PauseIcon style={{ fontSize: 20 }} />
              )}
              <div>
                {pauseResumeRequestInProgress
                  ? t("components:buttonLabels.gettingGps")
                  : t("stopPage:timeTracking.pause")}
              </div>
            </>
          )}
        </button>

        {tracking.ended ? (
          <button
            disabled={requestInProgress || !!(!tracking.started || latestPause)}
            onClick={onStopTrackingClick}
            className="time-tracking-button flex-grow gray"
          >
            <div className="flex flex-col items-end">
              <div>{t("stopPage:timeTracking.stopped")}</div>
              <div className="time-tracking-button__date">
                {DateTime.fromISO(tracking.ended).toFormat("HH:mm")}
              </div>
            </div>
            <div>
              {stopRequestInProgress ? (
                <LoadingSpinner />
              ) : (
                <EditOutlinedIcon style={{ fontSize: 22 }} />
              )}
            </div>
          </button>
        ) : (
          <button
            disabled={requestInProgress || !!(!tracking.started || latestPause)}
            onClick={onStopTrackingClick}
            className="time-tracking-button flex-grow red"
          >
            {stopRequestInProgress ? (
              <LoadingSpinner />
            ) : (
              <FileDownloadDoneIcon style={{ fontSize: 20 }} />
            )}
            <div>
              {stopRequestInProgress
                ? t("components:buttonLabels.gettingGps")
                : t("stopPage:timeTracking.stop")}
            </div>
          </button>
        )}
      </div>
      {errorKey && (
        <Alert severity="error" onClose={() => setErrorKey(null)}>
          {t(`translation:${errorKey}`)}
        </Alert>
      )}
      <TotalCargoDriverTime cargoId={cargoId} />
    </>
  );
};

const LoadingSpinner = () => (
  <CircularProgress style={{ color: "white" }} size={22} />
);

const EditTimeTrackingValueDialog = (props: {
  type: "START" | "END";
  dateTime: DateTime;
  pauseResumeEventsTimes: DateTime[];
  cargoId: string;
  stopId: string;
  onClose: () => void;
}) => {
  const { onClose, cargoId, dateTime, type, pauseResumeEventsTimes, stopId } =
    props;
  const { t } = useTranslation(["stopPage", "translation"]);
  const dispatch = useAppDispatch();
  const { reloadData } = useRoutePageContext();
  const [isSaving, setIsSaving] = useState(false);
  const [saveErrorKey, setSaveErrorKey] = useState<ActionErrorKey | null>(
    null
  );
  const [dateTimeError, setDateTimeError] = useState<DateTime | null>(null);
  const [time, setTime] = useState<string>(dateTime.toFormat("HH:mm"));
  const [date, setDate] = useState<DateTime>(dateTime);

  const params = useParams();
  const routeId = params.routeId as string;
  const truckId = params.truckId as string;

  const onSaveClick = async () => {
    setIsSaving(true);
    setDateTimeError(null);
    setSaveErrorKey(null);
    const dateTimeNew = date.set({
      hour: parseInt(time.split(":")[0]),
      minute: parseInt(time.split(":")[1]),
    });

    if (pauseResumeEventsTimes.length) {
      if (type === "START") {
        const maxDateTime = DateTime.min(...pauseResumeEventsTimes);
        if (maxDateTime && dateTimeNew > maxDateTime) {
          setDateTimeError(maxDateTime);
          setIsSaving(false);
          return;
        }
      } else {
        const minDateTime = DateTime.max(...pauseResumeEventsTimes);
        if (minDateTime && dateTimeNew < minDateTime) {
          setDateTimeError(minDateTime);
          setIsSaving(false);
          return;
        }
      }
    }

    try {
      const action =
        type === "START" ? startCargoTimeTracking : stopCargoTimeTracking;
      await dispatch(
        action({
          cargoId,
          data: { time: dateTimeNew, routeId, truckId, stopId },
        })
      ).unwrap();
      onClose();
    } catch (err) {
      setSaveErrorKey(handleActionError(err));
      reloadData();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onClose={() => !isSaving && onClose()}>
      <div style={{ minWidth: 290 }}>
        <DialogTitle>{t("timeTracking.editDialogTitle")}</DialogTitle>
        <DialogContent>
          {dateTimeError && (
            <div style={{ color: "red", marginBottom: 10 }}>
              {t(
                `timeTracking.${type === "START" ? "errorStartTimeTooHigh" : "errorStopTimeTooLow"}` as any,
                {
                  dateTime: dateTimeError.toFormat("dd.MM.yyyy HH:mm"),
                }
              )}
            </div>
          )}
          {saveErrorKey && (
            <Alert severity="error" style={{ marginBottom: 10 }}>
              {t(`translation:${saveErrorKey}`)}
            </Alert>
          )}
          <div>{t("timeTracking.date")}:</div>
          <DateInput
            value={date.toISODate()}
            onChange={(e) => {
              if (!e.target.value) return;
              setDate(DateTime.fromISO(e.target.value));
            }}
          />
          <div>{t("timeTracking.time")}:</div>
          <TimeInput
            value={time}
            onChange={(e) => {
              if (!e.target.value) return;
              const time = DateTime.fromFormat(e.target.value, "HH:mm");
              setTime(time.toFormat("HH:mm"));
            }}
          />
        </DialogContent>
        <DialogActions className="mx-4 mb-2">
          <Button
            disabled={isSaving}
            onClick={onClose}
            variant="outlined"
            size="large"
            fullWidth
          >
            {t("timeTracking.cancel")}
          </Button>
          <LoadingButton
            disabled={isSaving}
            loading={isSaving}
            variant="contained"
            fullWidth
            size="large"
            onClick={onSaveClick}
          >
            {t("timeTracking.save")}
          </LoadingButton>
        </DialogActions>
      </div>
    </Dialog>
  );
};

export default TimeTracking;
